import { useState } from "react";
import { useNotify } from "ra-core";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  formatSummaryText,
  requestSummary,
  saveSummaryAsNote,
  type CopilotContext,
} from "./copilotSuggest";
import { deleteTranscriptText } from "./transcriptConsent";
import { HelpTooltip } from "../help/HelpTooltip";

/**
 * Resumen final (Bloque C2, paso 5): al detener la transcripción. Por defecto NO conserva la
 * transcripción completa (se borra al guardar la nota, reutilizando "Borrar transcripción de
 * esta llamada" del paso 2) — solo si Ian marca "Conservar transcripción" se deja, sujeta al
 * plazo de retención de Ajustes.
 */
export const CopilotSummaryPanel = ({
  consentId,
  contactId,
  salesId,
  transcript,
  context,
  onTranscriptCleared,
}: {
  consentId: number;
  contactId: number | null;
  salesId: number | null;
  transcript: string;
  context: CopilotContext;
  onTranscriptCleared: () => void;
}) => {
  const notify = useNotify();
  const [text, setText] = useState<string | null>(null);
  const [keep, setKeep] = useState(false);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    const { data, error } = await requestSummary(
      consentId,
      transcript,
      context,
    );
    if (error) notify(error, { type: "error" });
    else if (data) setText(formatSummaryText(data));
    setBusy(false);
  };

  const save = async () => {
    if (!contactId || !salesId || !text?.trim()) return;
    setBusy(true);
    try {
      await saveSummaryAsNote(contactId, salesId, text.trim());
      notify("Resumen guardado como nota", { type: "success" });
      if (!keep) {
        await deleteTranscriptText(consentId);
        onTranscriptCleared();
      }
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setBusy(false);
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border p-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <b className="inline-flex items-center gap-1 text-sm">
          Resumen de la llamada
          <HelpTooltip topic="calls.summary" />
        </b>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox checked={keep} onCheckedChange={(c) => setKeep(!!c)} />
            Conservar transcripción
          </label>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={generate}
          >
            {busy && text == null ? "Generando…" : "Generar resumen"}
          </Button>
        </div>
      </div>
      {text != null && (
        <>
          <Textarea
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Button
            size="sm"
            className="self-start"
            disabled={busy || !contactId}
            onClick={save}
          >
            Guardar como nota en el contacto
          </Button>
          {!contactId && (
            <span className="text-xs text-[var(--critical)]">
              Esta empresa no tiene contacto: no se puede guardar la nota.
            </span>
          )}
        </>
      )}
    </div>
  );
};
